import { useRef, useState } from "react";
import { PrimeIcons } from "primereact/api";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";

import { Project } from "interfaces";

import {
  buttonDelete,
  buttonLeave,
  labelDelete,
  labelInfo,
  labelLeaveProject,
  labelRename,
  labelShare,
  modalConfirmDeleteProjectSubtitlePart1,
  modalConfirmDeleteProjectSubtitlePart2,
  modalConfirmDeleteProjectTitle,
  modalConfirmLeaveProjectSubTitlePart1,
  modalConfirmLeaveProjectSubTitlePart2,
  modalConfirmLeaveProjectTitle,
  snackbarProjectAbandonedSubTitlePart1,
  snackbarProjectAbandonedSubTitlePart2,
  snackbarProjectAbandonedTitle,
  snackbarProjectDeletedSubTitlePart1,
  snackbarProjectDeletedSubTitlePart2,
  snackbarProjectDeletedTitle,
} from "_constants";
import { useDetailPanelProvider } from "providers/DetailPanelProvider";
import { DetailPanelActions } from "components/DetailPanel/DetailPanelActions";
import { useConfirmDialogExit } from "hooks";
import { useSnackbar } from "providers/SnackbarProvider";
import { ProjectDetailPanelHeader } from "../../detailPanel/ProjectDetailPanelHeader";
import { Menu } from "primereact/menu";
import { DetailPanelSourcesContainer } from "../../detailPanel/DetailPanelSourcesContainer";
import { DetailPanelUserslist } from "../../detailPanel/DetailPanelUsersList";
import { deleteProject, getDetailProject, leaveProject } from "services";
import { useAppDispatch } from "store/store";
import { ButtonSetProjectPreferred } from "components/buttons";
import { HeaderCardSourcesTotalProject } from "./HeaderCardSourcesTotalProject";
import { ModalRenameProject } from "./actions/ModalRenameProject";
import {
  setCurrentProject,
  setPageProjects,
} from "store/slicer/GestioneProjectsSlice";
import { ModalShareProject } from "./actions";
import { getUserId } from "utils";

export const HeaderCardProject = ({ project }: { project: Project }) => {
  const dispatch = useAppDispatch();
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const { showPanel, hidePanel } = useDetailPanelProvider();
  const { showSnackbar } = useSnackbar();
  const { showConfirmDialogDelete } = useConfirmDialogExit();
  const menu = useRef<Menu>(null);

  const getProjectDetail = () => {
    if (project?.id) {
      getDetailProject(project.id)
        .then((response) => {
          dispatch(setCurrentProject(response.data as any));
          showDetailPanel(response.data);
        })
        .catch((_) => {});
    }
  };

  const showDetailPanel = (projectDetail: Project) => {
    showPanel({
      icons: (
        <>
          {project?.id && (
            <ButtonSetProjectPreferred
              projectId={project.id}
              isPreferred={projectDetail.isPreferred}
              customClass="width-px-28 height-px-28"
            />
          )}
          <DetailPanelActions
            items={[
              ...(project?.createdById === getUserId()
                ? [
                    {
                      label: labelShare,
                      icon: PrimeIcons.SHARE_ALT,
                      command: () => {
                        openShareModal();
                      },
                    },
                  ]
                : []),
              ...(project?.createdById === getUserId()
                ? [
                    {
                      label: labelRename,
                      icon: PrimeIcons.PENCIL,
                      command: () => {
                        setShowRenameModal(true);
                      },
                    },
                    {
                      separator: true,
                    },
                  ]
                : []),
              {
                label:
                  project?.createdById === getUserId()
                    ? labelDelete
                    : labelLeaveProject,
                icon: PrimeIcons.TRASH,
                className: "delete-color",
                command: () => {
                  openDeleteModal();
                },
              },
            ]}
          />
        </>
      ),
      header: <ProjectDetailPanelHeader />,
      body: (
        <>
          <DetailPanelSourcesContainer
            codesTotal={projectDetail.codesSourceTotal ?? 0}
            archivesTotal={projectDetail.archiveSourceTotal ?? 0}
            privatesTotal={projectDetail.privateSourceTotal ?? 0}
          />
          <DetailPanelUserslist
            usersList={projectDetail.users ?? []}
            project={project}
          />
        </>
      ),
    });
  };

  const openShareModal = () => {
    setShowShareModal(true);
  };

  const items = [
    ...(project?.createdById === getUserId()
      ? [
          {
            label: labelShare,
            icon: PrimeIcons.SHARE_ALT,
            command: () => {
              openShareModal();
            },
          },
        ]
      : []),
    {
      label: labelInfo,
      icon: PrimeIcons.INFO_CIRCLE,
      command: () => {
        getProjectDetail();
      },
    },
    ...(project?.createdById === getUserId()
      ? [
          {
            label: labelRename,
            icon: PrimeIcons.PENCIL,
            command: () => {
              setShowRenameModal(true);
            },
          },
        ]
      : []),

    {
      separator: true,
    },
    {
      label:
        project?.createdById === getUserId() ? labelDelete : labelLeaveProject,
      icon: PrimeIcons.TRASH,
      className: "delete-color",
      command: () => {
        openDeleteModal();
      },
    },
  ];

  const confirmDeleteProject = () => {
    if (project.id) {
      deleteProject(project.id)
        .then((_) => {
          showSnackbar({
            severity: "success",
            summary: snackbarProjectDeletedTitle,
            detail: `${snackbarProjectDeletedSubTitlePart1}"${project.name}" ${snackbarProjectDeletedSubTitlePart2}`,
            life: 5000,
          });
          dispatch(setPageProjects(-1));
          setTimeout(() => {
            dispatch(setPageProjects(1));
            hidePanel();
          }, 200);
        })
        .catch((_) => {});
    }
  };

  const confirmLeaveProject = () => {
    if (project.id) {
      leaveProject(project.id)
        .then((_) => {
          showSnackbar({
            severity: "success",
            summary: snackbarProjectAbandonedTitle,
            detail: `${snackbarProjectAbandonedSubTitlePart1}"${project.name}" ${snackbarProjectAbandonedSubTitlePart2}`,
            life: 5000,
          });
          dispatch(setPageProjects(-1));
          setTimeout(() => {
            dispatch(setPageProjects(1));
            hidePanel();
          }, 200);
        })
        .catch((_) => {});
    }
  };

  const openDeleteModal = () => {
    showConfirmDialogDelete({
      title:
        project?.createdById === getUserId()
          ? modalConfirmDeleteProjectTitle
          : modalConfirmLeaveProjectTitle,
      subtitle:
        project?.createdById === getUserId()
          ? `${modalConfirmDeleteProjectSubtitlePart1}<b class="capitalize">${project.name}</b>${modalConfirmDeleteProjectSubtitlePart2}`
          : `${modalConfirmLeaveProjectSubTitlePart1}<b class="capitalize">${project.name}</b>${modalConfirmLeaveProjectSubTitlePart2}`,
      rejectButtonLabel:
        project?.createdById === getUserId() ? buttonDelete : buttonLeave,
      rejectCallback:
        project?.createdById === getUserId()
          ? confirmDeleteProject
          : confirmLeaveProject,
      icon: PrimeIcons.USER_MINUS,
    });
  };

  const toggleMenu = (e: any) => {
    e.stopPropagation();
    menu.current!.toggle(e);
  };

  return (
    <>
      <div className="header">
        <div className="title ellipsis">
          <Tooltip target={`.custom-target`} position="bottom" />
          <span className=" custom-target " data-pr-tooltip={project.name}>
            {project.name}
          </span>
        </div>
        <div className="actions">
          {project?.id && (
            <ButtonSetProjectPreferred
              projectId={project.id}
              isPreferred={project.isPreferred}
            />
          )}
          <Menu model={items} popup ref={menu} className="tiered-menu-custom" />
          <Button
            icon={PrimeIcons.ELLIPSIS_V}
            text
            className="meatball"
            onClick={(e) => toggleMenu(e)}
          />
        </div>
      </div>
      <HeaderCardSourcesTotalProject
        codesSourceTotal={project.codesSourceTotal ?? 0}
        archiveSourceTotal={project.archiveSourceTotal ?? 0}
        privateSourceTotal={project.privateSourceTotal ?? 0}
      />

      {showRenameModal && (
        <ModalRenameProject
          project={project}
          showDialog={showRenameModal}
          onClose={() => setShowRenameModal(false)}
        />
      )}
      {showShareModal && (
        <ModalShareProject
          project={project}
          showDialog={showShareModal}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </>
  );
};
